<template>
  <v-dialog
    v-model="visible"
    :width="dialogWidth"
    persistent
  >
    <v-card
        class="resizable-card"
        :class="{ 'no-select': resizing }"
    >
      <v-card-title>
        <v-row class="no-gutters">
          <span class="headline">Preview {{rules.name}}</span>
          <v-spacer/>
          <v-btn color="green" class="ml-1" @click="exportToJSON">Export</v-btn>
          <v-btn color="blue" class="ml-1" @click="onPressImportJson">Import</v-btn>
        </v-row>
      </v-card-title>

      <v-card-text>
        <codemirror
          v-model="model"
          :options="cmOpt"
          class="cm-editor"
        />
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn
          color="blue darken-1"
          text
          @click="cancel"
        >
          Cancel
        </v-btn>
        <v-btn
            :disabled="rules.create === false && rules.update === false"
            @click="save"
            color="blue darken-1"
            text
        >
          Save
        </v-btn>
      </v-card-actions>

      <input ref="uploaderJson"
             class="d-none"
             type="file"
             accept="application/json"
             @change="onFileChangedJson">
      <div class="resize-handle" @mousedown="startResize"></div>
    </v-card>
  </v-dialog>
</template>

<script>
import VueTypes from "vue-types";
import CodeMirror from "codemirror";
import {ViewRules} from "@/common/ViewRules";

import { codemirror } from "vue-codemirror";
import "codemirror/lib/codemirror.css";
import "codemirror/theme/monokai.css";
import "codemirror/addon/edit/matchbrackets";

import "codemirror/addon/search/searchcursor";
import "codemirror/addon/search/match-highlighter";

import "codemirror/addon/lint/lint";
import "codemirror/addon/lint/lint.css";
import "codemirror/addon/lint/json-lint";

import "codemirror/mode/javascript/javascript.js";
import {deserialize, serialize} from "serializr";
import {FirebaseJsonUtils} from "@/common/FirebaseJsonUtils";

export default {
  name: "r-dialog-preview",
  props: {
    content: VueTypes.string.def(""),
  },
  components: { codemirror },
  data: () => ({
    visible: false,
    resolve: null,
    reject: null,
    model: "",
    modelUid: "",
    type: null,
    rules: ViewRules.READONLY,

    dialogWidth: 920,
    dialogHeight: 546,
    resizing: false,
    resizeStartX: 0,
    resizeStartY: 0,
    resizeStartWidth: 920,
    resizeStartHeight: 546,
    viewportPadding: 32,

    cmOpt: {
      mode: 'application/json',
      theme: 'monokai',
      tabSize: 4,
      lineNumbers: true,
      lineWrapping: true,
      viewportMargin: Infinity,
      matchBrackets: true,
      flattenSpans: true,
      highlightSelectionMatches: { showToken: true },
      gutters: ["CodeMirror-lint-markers"],
      lint: {
        tooltips: true,
        delay: 200,
        getAnnotations: (text, options, cm) => {
          const s = (text || "").trim();
          if (!s) return [];
          return CodeMirror.lint.json(text, options, cm);
        }
      }
    },
  }),
  methods: {
    startResize(e) {
      this.resizing = true;
      this.resizeStartX = e.clientX;
      this.resizeStartY = e.clientY;
      this.resizeStartWidth = this.dialogWidth;
      this.resizeStartHeight = this.dialogHeight;

      window.addEventListener("mousemove", this.onResize);
      window.addEventListener("mouseup", this.stopResize);
    },
    onResize(e) {
      if (!this.resizing) return;

      const dx = e.clientX - this.resizeStartX;
      const dy = e.clientY - this.resizeStartY;

      let newWidth = this.resizeStartWidth + dx;
      let newHeight = this.resizeStartHeight + dy;

      const minWidth = 500;
      const minHeight = 350;

      if (newWidth < minWidth) newWidth = minWidth;
      if (newHeight < minHeight) newHeight = minHeight;

      this.dialogWidth = newWidth;
      this.dialogHeight = newHeight;
    },
    stopResize() {
      if (!this.resizing) return;
      this.resizing = false;

      window.removeEventListener("mousemove", this.onResize);
      window.removeEventListener("mouseup", this.stopResize);
    },
    save() {
      try {
        let parsed = JSON.parse(this.model);
        parsed.uid = this.modelUid;
        const result = deserialize(this.type, parsed);
        if (typeof result.resetToValidAutomatic === "function") {
          result.resetToValidAutomatic();
        }
        if (result.valid()) {
          this.resolve && this.resolve(result);
          this.visible = false;
        } else {
          this.$alert("Invalid Config!");
        }
      } catch (e) {
        this.$alert("Invalid JSON! " + e.message);
      }
    },
    cancel() {
      this.visible = false;
    },
    show(rules, input, type) {
      this.visible = true;
      this.rules = rules;
      this.modelUid = input.uid;
      this.type = type;
      this.model = JSON.stringify(serialize(type, input), null, 4);

      return new Promise((resolve, reject) => {
        this.resolve = resolve;
        this.reject = reject;
      });
    },
    exportToJSON() {
      try {
        // Parse the current model JSON
        const adminPanelJson = JSON.parse(this.model);

        // Convert to Firebase JSON format (compact)
        const firebaseJson = FirebaseJsonUtils.toFirebaseJson(adminPanelJson);

        // Create blob with Firebase JSON
        const jsonString = JSON.stringify(firebaseJson, null, 2);
        let blob = new Blob([jsonString], {type: "application/json"});
        let saver = require('file-saver');

        // Use project prefix for Firebase export naming convention
        const fileName = `mira-merge---export-${this.type.name.toLowerCase()}-${this.modelUid}.json`;
        saver.saveAs(blob, fileName);
      } catch (e) {
        this.$alert("Failed to export: " + e.message);
      }
    },
    onPressImportJson() {
      this.$refs.uploaderJson.click()
    },
    onFileChangedJson(e) {
      if (!e.target.files || !e.target.files[0]) {
        this.$refs.uploaderJson.value = "";
        return;
      }
      this.selectedFile = e.target.files[0]
      this.selectedFile.text()
          .then((data)=>{
            try {
              const importedJson = JSON.parse(data);

              // Detect format and handle accordingly
              const format = FirebaseJsonUtils.detectFormat(importedJson);

              let jsonToDeserialize = importedJson;

              // If it's Firebase format, we can deserialize directly
              // If it's Admin Panel format, it should also deserialize directly
              // serializr handles both cases

              // Restore UID if it was in the imported JSON
              if (importedJson.uid) {
                jsonToDeserialize.uid = this.modelUid;
              }

              let parsedData = deserialize(this.type, jsonToDeserialize);
              if (typeof parsedData.resetToValidAutomatic === "function") {
                parsedData.resetToValidAutomatic();
              }

              if (parsedData.valid()) {
                // Always display as full Admin Panel JSON format in the editor
                this.model = JSON.stringify(serialize(this.type, parsedData), null, 4);
                this.$alert(`Successfully imported ${format} format JSON`);
              } else {
                this.$alert("Invalid Config!");
              }
            } catch (error) {
              this.$alert("Failed to parse JSON: " + error.message);
            }
          }).catch((error) => {
            this.$alert("Failed to read file: " + error.message);
      });
      this.$refs.uploaderJson.value = "";
    },
  },
}

</script>

<style>
.CodeMirror {
  height: auto;
}

.cm-editor {
  border: 1px solid #fff;
  border-radius: 6px;
  font-size: 14px;
}

.CodeMirror-matchingbracket {
  background-color: rgba(0, 120, 255, 0.5);
  color: white;
}

.CodeMirror-nonmatchingbracket {
  background-color: rgba(255, 0, 0, 0.1);

}

.cm-matchhighlight {
  background: rgba(0, 150, 255, 0.2);
}

.CodeMirror-lint-tooltip {
  max-width: 520px;
  line-height: 1.3;
  white-space: pre-wrap;
  z-index: 99999;
}

.resizable-card {
  position: relative;
  display: flex;
  flex-direction: column;
}

.resizable-card__body {
  flex: 1;
  overflow: auto;
}

.resizable-card__actions {
  flex-shrink: 0;
}

.resize-handle {
  position: absolute;
  right: 0;
  bottom: 0;
  width: 14px;
  height: 14px;
  cursor: se-resize;
  border-right: 2px solid rgba(0, 0, 0, 0.3);
  border-bottom: 2px solid rgba(0, 0, 0, 0.3);
}
</style>